import {
  type Allocation,
  type Customer,
  type CustomerConfiguration,
  CustomerOrder,
  type DefaultMarketAccounts,
  DerivativeSettlementTypeEnum,
  EMPTY_ARRAY,
  type FeeModeEnum,
  type Field,
  type MarketAccount,
  ModeEnum,
  type NumericField,
  OptionTypeEnum,
  OrdTypeEnum,
  type Order,
  type OrderStrategy,
  type ParameterLike,
  type Security,
  isMultileg,
} from '@talos/kyoko';
import { compact, get, groupBy, map } from 'lodash-es';
import { isOrderTypeLimitAllIn } from '../../utils/security';

/**
 * Given an array of selectable market accounts, returns the default ones.
 *
 * This function groups the available market accounts by market, sorts each group by display name,
 * and then takes the default one or first one if no default one is defined from each market grouping
 */
export function getOneAccountPerMarket(
  marketAccounts: MarketAccount[],
  defaultMarketAccounts?: DefaultMarketAccounts
): MarketAccount[] {
  const groups = groupBy(marketAccounts, acc => acc.Market);
  // Map each group to one selection. Within each group we sort by display name to get the default or "top" one
  const oneSelectionPerMarket = map(groups, group => {
    const items = group.toSorted((a, b) => (a.DisplayName ?? a.Name).localeCompare(b.DisplayName ?? b.Name));

    // Get default Market Account for given Market...
    const defaultItem =
      defaultMarketAccounts != null
        ? items.find(marketAccount => get(defaultMarketAccounts, marketAccount.Market) === marketAccount.Name)
        : undefined;

    // ...or pick first item if not default
    return defaultItem ?? items.at(0);
  });

  return compact(oneSelectionPerMarket);
}

export function mapOrderToCustomerOrder(order: Order): CustomerOrder {
  const customerOrder = new CustomerOrder({
    ...order,
    // Remember: The ParentOrderID in Order is the OrderID in CustomerOrder
    OrderID: order.ParentOrderID || '',
    PricingParameters: order.customerPricingParams,
    Counterparty: order.customerOrder || '',
    MarketAccount: order.customerAccount || '',
    OrderQty: order.customerQuantity || '',
    Price: order.customerPrice || '',
    AvgPx: order.customerAvgPx || '',
    CustomerUser: '',
    AmountCurrency: order.AmountCurrency || '',
    CumAmt: order.CumAmt || '',
    ExecID: order.ExecID || '',
    LeavesQty: order.LeavesQty || '',
    Strategy: order.customerStrategy || order.Strategy,
    // Sales Order ETH-BTC translates to ETH-BTC:USD-2 in Principal land but remains as ETH-BTC in dealer
    Symbol: order.customerSymbol || order.Symbol,
    Parameters: order.customerParameters ?? undefined,
    LegSummary: undefined,
  });

  return customerOrder;
}

export const getOrdType = (
  showAllInPrices: FeeModeEnum | null | undefined,
  security: Security | undefined,
  strategy: OrderStrategy | undefined
): OrdTypeEnum => {
  if (strategy?.Name === 'Market') {
    return OrdTypeEnum.Market;
  }
  // OrdTypes are sent in StrategyParams as a TopLevelHidden just so we read the value and send it back
  const beOrdTypeParamHack: ParameterLike | undefined = strategy?.Parameters.find(p => p.Name === 'OrdType');
  if (beOrdTypeParamHack) {
    return beOrdTypeParamHack.DefaultValue as OrdTypeEnum;
  }
  return isOrderTypeLimitAllIn(showAllInPrices, security) ? OrdTypeEnum.LimitAllIn : OrdTypeEnum.Limit;
};

/* If Customer Configuration Filtering is enabled, we should only take the enabled customer configurations,
   then filter out any disabled customer pricing rules. Return the filtered customers list. */
export const getSelectableCustomers = (
  enabledCustomers: Customer[],
  customerConfigurations: CustomerConfiguration[]
) => {
  if (enabledCustomers.length === 0) {
    // Customers weren't loaded yet
    return [];
  }

  if (customerConfigurations.length === 0) {
    // Customer configurations weren't loaded yet, or are not being used.
    return enabledCustomers;
  }

  const enabledCustomersMapByName = new Map(enabledCustomers.map(c => [c.Name, c]));
  // Return only the customers that have enabled configurations
  return compact(
    customerConfigurations.map(cc => {
      // If the customer configuration is disabled, return null
      if (cc.Mode === ModeEnum.Disabled) {
        return null;
      }
      // Find the not disabled customer that matches the configuration
      const foundCustomerForConfiguration = enabledCustomersMapByName.get(cc.Counterparty);
      if (foundCustomerForConfiguration == null) {
        return null;
      }

      // Return the found customer
      return foundCustomerForConfiguration;
    })
  );
};

/** This function takes in an array of allocation "fields", and outputs standard allocations from these fields */
export function subAccountFieldsToAllocations(
  allocations: { subAccountField: Field; valueField: NumericField }[]
): Allocation[] {
  return allocations.length
    ? allocations.map(x => ({
        subAccount: x.subAccountField.value || '',
        value: x.valueField.displayValue || '',
      }))
    : EMPTY_ARRAY;
}

/**
 * Determines the cross currency for a multileg security by finding the common currency between its legs.
 *
 * @param security - The multileg security to analyze
 * @param securitiesBySymbol - A map of securities indexed by their symbol, used to look up leg details
 * @returns The cross currency symbol if found, or undefined if the security is not multileg or no common currency exists
 */
export const getCrossCurrency = (security: Security, securitiesBySymbol: Map<string, Security>): string | undefined => {
  if (!isMultileg(security) || !security.MultilegDetails) {
    return undefined;
  }

  const leg1Security = securitiesBySymbol.get(security.MultilegDetails.Legs[0].Symbol);
  const leg2Security = securitiesBySymbol.get(security.MultilegDetails.Legs[1].Symbol);
  const leg1Currencies = [leg1Security?.BaseCurrency, leg1Security?.QuoteCurrency];
  const leg2Currencies = [leg2Security?.BaseCurrency, leg2Security?.QuoteCurrency];

  // Return the first currency that is common to both arrays, or undefined if there is none.
  return leg1Currencies.find(currency => leg2Currencies.includes(currency));
};

export type CustomOptionSymbolInput = {
  market: string;
  underlyingCode: string;
  expiration: string;
  strikePrice: string;
  optionType: OptionTypeEnum;
  derivativeSettlementType?: DerivativeSettlementTypeEnum;
  settlementCurrency?: string;
};

// Currently we only support STS custom options, so this is hardcoded to that market's option symbol logic
export function customOptionSymbolInputToSymbol(input: CustomOptionSymbolInput): string | undefined {
  if (!input.derivativeSettlementType) {
    return undefined;
  }

  const [baseCurrency, quoteCurrency] = input.underlyingCode.split('-');
  if (!baseCurrency || !quoteCurrency) {
    return undefined;
  }

  const settlement =
    input.derivativeSettlementType === DerivativeSettlementTypeEnum.Physical ? 'PHYS' : input.settlementCurrency;
  if (!settlement) {
    return undefined;
  }

  const expiration = input.expiration.slice(0, 10).replaceAll('-', '');
  const optionType = input.optionType === OptionTypeEnum.Call ? 'C' : 'P';

  return `${input.market}:STS-${baseCurrency}-${quoteCurrency}-${settlement}-${expiration}-${
    input.strikePrice
  }-${optionType}-E-V`;
}
